import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  Spinner,
  Dropdown,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import LoadingSkeleton from "../../../../components/loading";
import useApi from "../../../../hooks/apiService";
import DetailsTab from "../detailsTabs";
import useWindowSize from "../../../../hooks/windowSize";
import { useTranslation } from "react-i18next";
import DynamometricChart, {
  CHART_COLORS,
} from "../../../../components/dynamometricChart";
import IconToSolve from "../../../../components/icon";
import { formatDateTimeSecondsBr } from "../../../../utils/dateFormatter";
import useDinamometricCard from "../../../../hooks/useDinamometricCard";
import { useMqttDinamometricCard } from "../../../../hooks/mqttService";
import "../general/detailsDynamometricCard.css";

const formatTime = (dateStr) =>
  new Date(dateStr).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

const groupEventsByDate = (events) => {
  const todayStr = new Date().toDateString();
  const map = new Map();

  events.forEach((event) => {
    const d = new Date(event.created_at);
    const key = d.toDateString();
    const label =
      key === todayStr
        ? "HOJE"
        : d
            .toLocaleDateString("pt-BR", { day: "numeric", month: "long" })
            .toUpperCase();
    if (!map.has(key)) map.set(key, { label, items: [] });
    map.get(key).items.push(event);
  });

  return Array.from(map.values());
};

const ChartHistory = () => {
  const { t } = useTranslation();
  const size = useWindowSize();
  const { assetSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchData } = useApi();
  const [assetData, setAssetData] = useState();

  const { lastNotification } = useMqttDinamometricCard();

  const {
    hasNewCard: dinHasNewCard,
    requesting: dinRequesting,
    requestCardGeneration,
    refreshCard,
    handleMqttNotification,
  } = useDinamometricCard(assetData?.id || null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCharts, setSelectedCharts] = useState([]);
  const [hasEverSelected, setHasEverSelected] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hasMore, setHasMore] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [inputStartDate, setInputStartDate] = useState(
    searchParams.get("start_date") || "",
  );
  const [inputEndDate, setInputEndDate] = useState(
    searchParams.get("end_date") || "",
  );
  const [appliedStartDate, setAppliedStartDate] = useState(
    searchParams.get("start_date") || "",
  );
  const [appliedEndDate, setAppliedEndDate] = useState(
    searchParams.get("end_date") || "",
  );

  const fetchEvents = (
    page = 1,
    limit = 5,
    assetID,
    startDate = "",
    endDate = "",
  ) => {
    setLoadingEvents(true);
    let url = `/dynamometer-cards/assets/${assetID}?page=${page}&items_per_page=${limit}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    fetchData(url)
      .then((response) => {
        setEvents(response.data || []);
        setHasMore(response.has_more);
        setTotalCount(response.total_count || 0);

        if (response.data?.length > 0 && !hasEverSelected) {
          handleCardClick(response.data[0]);
          setHasEverSelected(true);
        }
      })
      .catch((error) => {
        console.error("Erro ao buscar os dados da API:", error);
      })
      .finally(() => {
        setLoadingEvents(false);
      });
  };

  useEffect(() => {
    fetchData(`/assets/slug/${assetSlug}`)
      .then((response) => {
        setAssetData(response.data);
        fetchEvents(
          pageIndex,
          pageSize,
          response.data.id,
          appliedStartDate,
          appliedEndDate,
        );
      })
      .catch((error) => {
        console.error("Erro ao buscar os dados da API:", error);
      });

    setLoading(false);
  }, [pageIndex, pageSize, appliedStartDate, appliedEndDate]);

  useEffect(() => {
    if (
      lastNotification?.assetId &&
      assetData?.id &&
      lastNotification.assetId === assetData.id
    ) {
      handleMqttNotification();
    }
  }, [lastNotification, assetData?.id]);

  useEffect(() => {
    if (events.length > 0) {
      const eventIdFromUrl = searchParams.get("eventId");
      if (eventIdFromUrl) {
        const foundEvent = events.find((e) => e.id === eventIdFromUrl);
        if (foundEvent) {
          setSelectedCharts([foundEvent]);
        }
      }
    }
  }, [events, searchParams]);

  const handleCardClick = (event) => {
    setSelectedCharts((prev) => {
      const alreadySelected = prev.find((chart) => chart.id === event.id);
      if (alreadySelected) {
        return prev.filter((chart) => chart.id !== event.id);
      }
      return [...prev, event];
    });
  };

  const downloadChartData = () => {
    if (selectedCharts.length === 0) return;

    const csvContent = selectedCharts
      .map((chart, index) => {
        const parts = [];
        if (chart.surface_card) {
          parts.push(
            chart.surface_card.position
              .map(
                (pos, i) =>
                  `Surface Card ${index + 1},${pos},${chart.surface_card.load[i]}`,
              )
              .join("\n"),
          );
        }
        if (chart.downhole_card) {
          parts.push(
            chart.downhole_card.position
              .map(
                (pos, i) =>
                  `Downhole Card ${index + 1},${pos},${chart.downhole_card.load[i]}`,
              )
              .join("\n"),
          );
        }
        return parts.join("\n");
      })
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "chart_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleApplyDateFilter = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (inputStartDate) next.set("start_date", inputStartDate);
      else next.delete("start_date");
      if (inputEndDate) next.set("end_date", inputEndDate);
      else next.delete("end_date");
      return next;
    });
    setAppliedStartDate(inputStartDate);
    setAppliedEndDate(inputEndDate);
    setSelectedCharts([]);
    setPageIndex(1);
  };

  const filteredEvents = useMemo(() => {
    let result = events;
    if (activeFilter === "cpm") result = result.filter((e) => e.cpm > 0);
    if (activeFilter === "inactive")
      result = result.filter((e) => !e.cpm || e.cpm === 0);
    if (search.trim()) {
      const term = search.trim().toLowerCase();
      result = result.filter((e) =>
        formatTime(e.created_at).toLowerCase().includes(term),
      );
    }
    return result;
  }, [events, activeFilter, search]);

  const groupedEvents = useMemo(
    () => groupEventsByDate(filteredEvents),
    [filteredEvents],
  );

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  const filters = [
    { key: "all", label: t("Todas") },
    { key: "cpm", label: t("Com CPM > 0") },
    { key: "inactive", label: t("Inativas") },
  ];

  return (
    <div
      className="d-flex flex-column"
      style={{ height: "90vh", overflow: "hidden", padding: "1rem" }}
    >
      {/* Top bar: back button + asset name + tabs */}
      <div className="d-flex align-items-center mb-3" style={{ flexShrink: 0 }}>
        <span
          className="me-3"
          style={{ fontSize: "18px", fontWeight: "bold", textWrap: "nowrap" }}
        >
          {assetData?.name}
        </span>
        <DetailsTab />
      </div>

      {loading ? (
        <LoadingSkeleton />
      ) : (
        <div
          className="d-flex flex-grow-1 overflow-hidden"
          style={{
            minHeight: 0,
            border: "1px solid #dee2e6",
            borderRadius: 8,
          }}
        >
          {/* ── Left panel ─────────────────────────────────────── */}
          <div
            className="d-flex flex-column border-end"
            style={{
              width: "25%",
              minWidth: 220,
              flexShrink: 0,
              background: "#fff",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div className="px-3 pt-3 pb-2 border-bottom">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="fw-semibold" style={{ fontSize: 15 }}>
                  {t("Cartas registradas")}
                </span>
                <span
                  className="badge rounded-pill"
                  style={{
                    backgroundColor: "#004196",
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {totalCount}
                </span>
              </div>

              {/* Date range filter */}
              <div className="d-flex align-items-end mt-1" style={{ gap: 6 }}>
                <div className="d-flex flex-column" style={{ flex: 1, gap: 2 }}>
                  <span className="text-muted" style={{ fontSize: 10 }}>
                    {t("De")}
                  </span>
                  <input
                    type="date"
                    value={inputStartDate}
                    onChange={(e) => setInputStartDate(e.target.value)}
                    className="form-control form-control-sm"
                    style={{ fontSize: 11 }}
                  />
                </div>
                <div className="d-flex flex-column" style={{ flex: 1, gap: 2 }}>
                  <span className="text-muted" style={{ fontSize: 10 }}>
                    {t("Até")}
                  </span>
                  <input
                    type="date"
                    value={inputEndDate}
                    onChange={(e) => setInputEndDate(e.target.value)}
                    className="form-control form-control-sm"
                    style={{ fontSize: 11 }}
                  />
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleApplyDateFilter}
                  disabled={loadingEvents}
                  style={{ fontSize: 11, flexShrink: 0 }}
                  title={t("Buscar")}
                >
                  <i className="bi bi-search" />
                </Button>
              </div>
            </div>

            {/* Event list */}
            <div style={{ overflowY: "auto", flex: 1 }}>
              {loadingEvents ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: 200 }}
                >
                  <Spinner />
                </div>
              ) : filteredEvents.length === 0 ? (
                <div
                  className="text-muted text-center p-4"
                  style={{ fontSize: 13 }}
                >
                  {t("Nenhuma carta encontrada")}
                </div>
              ) : (
                groupedEvents.map(({ label, items }) => (
                  <div key={label}>
                    {/* Date section header */}
                    <div
                      className="px-3 py-1 fw-semibold border-bottom border-top"
                      style={{
                        fontSize: 11,
                        color: "#6c757d",
                        backgroundColor: "#f8f9fa",
                        letterSpacing: "0.03em",
                      }}
                    >
                      {label} · {items.length}{" "}
                      {items.length === 1 ? t("carta") : t("cartas")}
                    </div>

                    {/* Event rows */}
                    {items.map((event) => {
                      const isSelected = !!selectedCharts.find(
                        (c) => c.id === event.id,
                      );
                      const pontos = event.surface_card?.position?.length ?? 0;

                      return (
                        <div
                          key={event.id}
                          className="d-flex align-items-center px-3 py-2 border-bottom"
                          onClick={() => handleCardClick(event)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: isSelected
                              ? "#e8f0fe"
                              : "transparent",
                            borderLeft: isSelected
                              ? "3px solid #004196"
                              : "3px solid transparent",
                            transition: "background-color 0.15s",
                          }}
                        >
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            className="me-3"
                            style={{ cursor: "pointer", flexShrink: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => handleCardClick(event)}
                          />

                          {/* Time + points */}
                          <div className="flex-grow-1">
                            <div
                              className="fw-semibold"
                              style={{ fontSize: 14, lineHeight: 1.2 }}
                            >
                              {formatTime(event.created_at)}
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: 11 }}
                            >
                              {pontos} {t("pontos")}
                            </div>
                          </div>

                          {/* CPM */}
                          <div
                            className="text-end ms-2"
                            style={{ flexShrink: 0 }}
                          >
                            <div
                              className="fw-bold"
                              style={{ fontSize: 14, lineHeight: 1.2 }}
                            >
                              {event.cpm != null ? event.cpm.toFixed(2) : "—"}
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: 10 }}
                            >
                              CPM
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2 border-top"
              style={{ fontSize: 12 }}
            >
              <span className="text-muted">
                {(pageIndex - 1) * pageSize + 1}–
                {Math.min(pageIndex * pageSize, totalCount)} {t("de")}{" "}
                {totalCount}
              </span>

              <div className="d-flex align-items-center gap-1">
                <Button
                  className="pagination-btn"
                  onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
                  disabled={pageIndex === 1 || loadingEvents}
                  title={t("Página anterior")}
                >
                  <i className="bi bi-chevron-left pagination-icon" />
                </Button>
                <span className="mx-1">
                  {pageIndex}/{totalPages}
                </span>
                <Button
                  className="pagination-btn"
                  onClick={() => setPageIndex((p) => p + 1)}
                  disabled={!hasMore || loadingEvents}
                  title={t("Próxima página")}
                >
                  <i className="bi bi-chevron-right pagination-icon" />
                </Button>
              </div>

              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPageIndex(1);
                }}
                className="pagination-select"
                disabled={loadingEvents}
                style={{ height: 28 }}
              >
                {[10, 20, 50].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Right panel ────────────────────────────────────── */}
          <div
            className="d-flex flex-column flex-grow-1"
            style={{ minWidth: 0, background: "#fff", overflow: "hidden" }}
          >
            {/* Fixed header */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
              <div className="d-flex align-items-center gap-2">
                <span className="fw-semibold" style={{ fontSize: 15 }}>
                  {t("Comparativo de cartas")}
                </span>
                <span
                  className="d-flex align-items-center gap-1 text-muted"
                  style={{ fontSize: 12 }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: CHART_COLORS[0].surface,
                      flexShrink: 0,
                    }}
                  />
                  {t("Superfície")}
                  <span className="mx-1">×</span>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: CHART_COLORS[0].downhole,
                      flexShrink: 0,
                    }}
                  />
                  {t("Fundo")}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn-table"
                  onClick={downloadChartData}
                  disabled={selectedCharts.length === 0}
                  title={t("Baixar CSV")}
                  style={{ opacity: selectedCharts.length === 0 ? 0.4 : 1 }}
                >
                  <IconToSolve iconName={"Csv"} width={35} height={35} />
                </button>

                <Dropdown as={ButtonGroup} size="sm">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip className="tooltip-alarm">
                        {dinHasNewCard
                          ? t(
                              "Nova carta disponível. Clique para atualizar a lista.",
                            )
                          : t(
                              "Este botão será liberado quando uma nova carta estiver disponível.",
                            )}
                      </Tooltip>
                    }
                  >
                    <Button
                      variant={dinHasNewCard ? "success" : "outline-secondary"}
                      onClick={() => {
                        refreshCard();
                        setSelectedCharts([]);
                        if (pageIndex === 1) {
                          fetchEvents(
                            1,
                            pageSize,
                            assetData.id,
                            appliedStartDate,
                            appliedEndDate,
                          );
                        } else {
                          setPageIndex(1);
                        }
                      }}
                      disabled={!dinHasNewCard}
                      className="d-flex align-items-center gap-1"
                      style={{ fontSize: 12 }}
                    >
                      <i
                        className={`bi ${dinHasNewCard ? "bi-arrow-clockwise" : "bi-hourglass-split"}`}
                      />
                      {dinHasNewCard ? t("Buscar carta") : t("Aguardando...")}
                    </Button>
                  </OverlayTrigger>
                  <Dropdown.Toggle
                    variant={dinHasNewCard ? "success" : "outline-secondary"}
                    className="dyn-split-toggle dropdown-toggle-split"
                    style={{ padding: "0.25rem 0.375rem" }}
                  >
                    <i className="bi bi-chevron-down" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={requestCardGeneration}
                      disabled={dinRequesting}
                      className="d-flex align-items-center py-0 gap-2"
                      style={{ fontSize: 13 }}
                    >
                      {dinRequesting ? (
                        <Spinner size="sm" />
                      ) : (
                        <i className="bi bi-arrow-repeat" />
                      )}
                      {t("Solicitar Carta")}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>

            {selectedCharts.length === 0 ? (
              /* Empty state */
              <div
                className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-center p-4"
                style={{ minHeight: "55vh" }}
              >
                <i
                  className="bi bi-graph-up"
                  style={{ fontSize: 52, color: "#c5d5ea" }}
                />
                <h6 className="mt-3 fw-semibold">
                  {t("Selecione uma carta para visualizar")}
                </h6>
                <p
                  className="text-muted"
                  style={{ maxWidth: 320, fontSize: 13 }}
                >
                  {t(
                    "Escolha uma ou mais cartas na lista ao lado para comparar as curvas de superfície e fundo.",
                  )}
                </p>
              </div>
            ) : (
              <div
                className="d-flex flex-column flex-grow-1 p-3"
                style={{ minHeight: 0 }}
              >
                {/* Selected chart chips */}
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {selectedCharts.map((chart, index) => {
                    const colors = CHART_COLORS[index % CHART_COLORS.length];
                    return (
                      <div
                        key={chart.id}
                        className="d-flex align-items-center gap-2 px-2 py-1 rounded"
                        style={{
                          fontSize: 11,
                          backgroundColor: "#f8f9fa",
                          border: `1.5px solid ${colors.surface}`,
                          color: "#495057",
                        }}
                      >
                        <span className="d-flex gap-1">
                          <span
                            title={t("Carta de superfície")}
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: colors.surface,
                              flexShrink: 0,
                            }}
                          />
                          <span
                            title={t("Carta de fundo")}
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: colors.downhole,
                              flexShrink: 0,
                            }}
                          />
                        </span>
                        <span className="text-nowrap">
                          <i
                            className="bi bi-calendar3 me-1"
                            style={{ color: "#6c757d" }}
                          />
                          {formatDateTimeSecondsBr(new Date(chart.created_at))}
                        </span>
                        {chart.cpm != null && (
                          <span className="text-nowrap">
                            {t("CPM")}: <strong>{chart.cpm.toFixed(2)}</strong>
                          </span>
                        )}
                        <button
                          className="btn btn-link p-0 lh-1"
                          style={{ color: "#adb5bd", fontSize: 14 }}
                          onClick={() => handleCardClick(chart)}
                          title={t("Remover")}
                        >
                          <i className="bi bi-x" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Chart */}
                <div className="flex-grow-1" style={{ minHeight: 0 }}>
                  <DynamometricChart data={selectedCharts} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartHistory;
